import { computed } from 'vue'
import type {
  PropertyType,
  PropertySolarbotUnitType,
  PropertyUnitType,
  PropertyVacationType
} from '@/interfaces'
import { usePropertiesQuery } from '@/composables/queries/properties'
import { useCurrentPropertyId } from '@/composables/use-current-property-id'
import { getPropertyTimeZone } from '@/composables/date-formatter'
import {
  isDisplayableProperty,
  selectableProperties
} from '@/utils/displayable-property'

export const useCurrentProperty = () => {
  const { currentPropertyId, setCurrentProperty } = useCurrentPropertyId()
  const { data, isPending, isSuccess } = usePropertiesQuery()

  const properties = computed<PropertyType[]>(() => data.value ?? [])

  const hasProperties = computed<boolean>(() => properties.value.length > 0)

  const selectablePropertyIds = computed<number[]>(() =>
    selectableProperties(properties.value).map((property) => property.id)
  )

  const displayableProperties = computed<PropertyType[]>(() =>
    properties.value.filter(isDisplayableProperty)
  )

  const hasMultipleDisplayableProperties = computed<boolean>(
    () => displayableProperties.value.length > 1
  )

  const currentProperty = computed<PropertyType | undefined>(() =>
    properties.value?.find((p) => p.id === currentPropertyId.value)
  )

  const currentPropertySolarbotUnits = computed<PropertySolarbotUnitType[]>(
    () => currentProperty.value?.solarbot_units ?? []
  )

  const currentPropertyHotbotUnits = computed<PropertyUnitType[]>(
    () => currentProperty.value?.units ?? []
  )

  const hasMultipleUnitsByCurrentProperty = computed<boolean>(
    () => currentPropertyHotbotUnits.value.length > 1
  )

  const hasSolarbotOnCurrentProperty = computed<boolean>(
    () => currentPropertySolarbotUnits.value.length > 0
  )

  const isCurrentPropertyHotbotOnly = computed<boolean>(() => {
    if (!isSuccess.value) return true
    return (
      currentPropertySolarbotUnits.value.length === 0 &&
      currentPropertyHotbotUnits.value.length > 0
    )
  })

  const currentPropertyVacation = computed<PropertyVacationType | null>(
    () => currentProperty.value?.vacation ?? null
  )

  const currentPropertyTimeZone = computed<string>(() =>
    getPropertyTimeZone(currentProperty.value?.time_zone)
  )

  const currentPropertyVacationRange = computed<Date[]>(() => {
    const vacation = currentPropertyVacation.value
    if (!vacation) return []
    return [new Date(vacation.starts_at), new Date(vacation.ends_at)]
  })

  const currentPropertyHasActiveVacation = computed<boolean>(() => {
    const [starts_at, ends_at] = currentPropertyVacationRange.value
    if (!starts_at || !ends_at) return false
    const now = new Date()
    return now >= starts_at && now < ends_at
  })

  return {
    properties,
    isPending,
    isSuccess,
    hasProperties,
    setCurrentProperty,
    selectablePropertyIds,
    currentPropertyId,
    currentProperty,
    displayableProperties,
    hasMultipleDisplayableProperties,
    currentPropertySolarbotUnits,
    currentPropertyHotbotUnits,
    hasMultipleUnitsByCurrentProperty,
    hasSolarbotOnCurrentProperty,
    isCurrentPropertyHotbotOnly,
    currentPropertyVacation,
    currentPropertyVacationRange,
    currentPropertyHasActiveVacation,
    currentPropertyTimeZone
  }
}
